import { Box, IconButton, ListItemIcon, ListItemText, makeStyles, Menu, MenuItem, Theme } from '@material-ui/core';
import { MoneyOff, MoreVert, Replay } from '@material-ui/icons';
import { useDocumentStore } from '@roc/feature-documents';
import { DataGrid, GridColumnProps } from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { isNil } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { INVOICE_COLUMNS } from '../paReconciliationColumns';
import { PaReconciliationRecord } from '../paReconciliationTypes';

// Grid sizing: the table grows with its rows up to MAX_TABLE_HEIGHT, then scrolls vertically (header stays fixed)
const ROW_HEIGHT = 46;
const HEADER_HEIGHT = 48;
const SCROLLBAR_ALLOWANCE = 20; // horizontal scrollbar + borders
const MAX_TABLE_HEIGHT = 480; // ~9 visible rows

const useStyles = makeStyles((theme: Theme) => ({
  gridContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));

// Same pattern as feature-special-servicing grids: DataGrid + MoreVert action cell + a single MUI Menu
const columns: GridColumnProps[] = [
  ...INVOICE_COLUMNS.map(column => ({
    field: column.field,
    headerName: column.headerName,
    minWidth: column.minWidth,
    cellRenderer: column.type === 'currency' ? 'currencyCellRenderer' : 'textCellRenderer',
  })),
  {
    field: 'rowId',
    headerName: 'Actions',
    cellRenderer: 'actionCellRenderer',
    minWidth: 90,
    width: 90,
    maxWidth: 90,
    sortable: false,
    pinned: 'right',
    lockPinned: true,
  },
];

interface IPaReconciliationInvoicesTableProps {
  type: string;
}

export const PaReconciliationInvoicesTable = observer(({ type }: IPaReconciliationInvoicesTableProps) => {
  const classes = useStyles();
  const {
    documentStore: {
      documentFormStore: { paReconciliationStore },
    },
  } = useDocumentStore();

  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const [record, setRecord] = useState<PaReconciliationRecord>(null);
  const rows = paReconciliationStore.selectedTypeRecords;
  const tableHeight = Math.min(HEADER_HEIGHT + Math.max(rows.length, 1) * ROW_HEIGHT + SCROLLBAR_ALLOWANCE, MAX_TABLE_HEIGHT);

  const openMenu = (event: React.MouseEvent<HTMLElement>, row: PaReconciliationRecord) => {
    setRecord(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const frameworkComponents = {
    textCellRenderer: params => <span>{isNil(params.value) || params.value === '' ? '-' : params.value}</span>,
    currencyCellRenderer: params => (
      <Box textAlign="right">{isNil(params.value) ? '-' : formatCurrency(Number(params.value))}</Box>
    ),
    actionCellRenderer: params => (
      <IconButton size="small" onClick={e => openMenu(e, params.node.data)}>
        <MoreVert color="primary" />
      </IconButton>
    ),
  };

  return (
    <Box className={classes.gridContainer} style={{ height: tableHeight }}>
      <DataGrid
        key={type}
        columns={columns}
        rows={rows}
        rowData={rows}
        rowModelType="clientSide"
        domLayout="normal"
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        frameworkComponents={frameworkComponents}
        getRowNodeId={row => row.rowId}
        suppressRowClickSelection
        showPagination={false}
      />
      <Menu
        id="pa-reconciliation-actions-menu"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            // TODO: wire action (paReconciliationStore.markWrittenOff(record.rowId)) once the behavior is defined
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <MoneyOff fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark written off</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            // TODO: wire action (paReconciliationStore.markReimbursed(record.rowId)) once the behavior is defined
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Replay fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark reimbursed</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
});
